import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { theme } from '../../../styles/theme';
import { WebsitePrimaryButton } from '../../common/websiteSection';
import overviewPreview from '../../hero/images/overviewpreview.png';
import calendarPreview from '../../hero/images/calendarpreview.png';
import HeroPreviewLightbox from './HeroPreviewLightbox';
import PreviewClickableImage from './PreviewClickableImage';

const flatCtaStyles = `
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  background: var(--login-primary);

  &:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
    background: var(--login-primary-dark);
    text-decoration: none;
  }
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(2.5rem, 6vw, 4.5rem) ${theme.spacing.xl} clamp(3rem, 7vw, 5rem);
  background:
    radial-gradient(ellipse 100% 80% at 15% -15%, rgba(72, 138, 236, 0.16) 0%, transparent 52%),
    radial-gradient(ellipse 70% 55% at 95% 5%, rgba(107, 163, 245, 0.12) 0%, transparent 58%),
    radial-gradient(ellipse 55% 40% at 50% 100%, rgba(72, 138, 236, 0.07) 0%, transparent 55%),
    #ffffff;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(15, 23, 42, 0.055) 1px, transparent 1px);
    background-size: 22px 22px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.9) 0%, transparent 88%);
    pointer-events: none;
  }
`;

const HeroContainer = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1140px;
  margin: 0 auto;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.08fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing['2xl']};
  }
`;

const HeroContent = styled.div`
  text-align: left;

  @media (max-width: 960px) {
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 ${theme.spacing.lg};
  font-size: clamp(2.25rem, 5vw, 3.65rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.08;
  color: var(--login-form-text);
`;

const HeroSubtitle = styled.p`
  margin: 0 0 ${theme.spacing.xl};
  max-width: 38ch;
  font-size: clamp(1.0625rem, 2vw, 1.25rem);
  line-height: 1.65;
  color: var(--login-form-muted);

  @media (max-width: 960px) {
    max-width: 42ch;
    margin-inline: auto;
  }
`;

const HeroHighlights = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin: 0 0 ${theme.spacing.xl};
  padding: 0;
  list-style: none;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const HeroHighlight = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid var(--login-form-border);
  background: rgba(255, 255, 255, 0.82);
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: var(--login-form-text);
  backdrop-filter: blur(8px);

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--login-primary);
    flex-shrink: 0;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const PillButton = styled(WebsitePrimaryButton)`
  border-radius: 999px;
  padding: 16px 32px;
  font-size: 1.0625rem;
  ${flatCtaStyles}
`;

const GhostPillLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 28px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  color: var(--login-form-text);
  border: 1.5px solid var(--login-form-border);
  background: rgba(255, 255, 255, 0.75);
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: var(--login-primary);
    color: var(--login-primary);
  }
`;

const HeroNote = styled.p`
  margin: ${theme.spacing.md} 0 0;
  font-size: ${theme.typography.fontSize.sm};
  color: var(--login-form-muted);
`;

const HeroVisual = styled.div`
  position: relative;
  min-height: 280px;

  @media (max-width: 960px) {
    max-width: 560px;
    margin: 0 auto;
  }
`;

const HeroMockupBack = styled.div`
  position: absolute;
  top: 8%;
  right: -2%;
  width: 72%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--login-form-border);
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.1);
  transform: rotate(4deg);
  opacity: 0.72;
  z-index: 0;

  @media (max-width: 960px) {
    display: none;
  }
`;

const BrowserFrame = styled.div`
  position: relative;
  z-index: 1;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--login-form-border);
  background: #fff;
  box-shadow:
    0 28px 64px rgba(15, 23, 42, 0.14),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
`;

const BrowserBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: 12px 16px;
  background: var(--login-form-input-bg);
  border-bottom: 1px solid var(--login-form-border);
`;

const BrowserDots = styled.div`
  display: flex;
  gap: 6px;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(15, 23, 42, 0.12);

    &:first-of-type {
      background: #ff5f57;
    }

    &:nth-of-type(2) {
      background: #febc2e;
    }

    &:nth-of-type(3) {
      background: #28c840;
    }
  }
`;

const BrowserUrl = styled.div`
  flex: 1;
  padding: 7px 14px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid var(--login-form-border);
  font-size: ${theme.typography.fontSize.sm};
  color: var(--login-form-muted);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BrowserScreen = styled.div`
  background: #0f172a;
`;

const HeroGlow = styled.div`
  position: absolute;
  inset: 10% 5% -8%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(72, 138, 236, 0.18) 0%, transparent 70%);
  filter: blur(40px);
  pointer-events: none;
  z-index: 0;
`;

const HERO_HIGHLIGHTS = [
  'Online booking',
  'Staff scheduling',
  'Automated reminders',
];

function DemoButton({ children }) {
  return <GhostPillLink to="/book-demo">{children}</GhostPillLink>;
}

export default function MarketingHero({ heroCtaLabel, trialEnabled }) {
  const [lightbox, setLightbox] = useState(null);

  const openPreview = (src, alt, dialogAriaLabel) => {
    setLightbox({ src, alt, dialogAriaLabel });
  };

  return (
    <Hero>
      <HeroContainer>
        <HeroGrid>
          <HeroContent>
            <HeroTitle>The next level of scheduling for service businesses.</HeroTitle>
            <HeroSubtitle>
              Online booking, staff calendars, and client management — everything your team
              needs to stay organized and fully booked.
            </HeroSubtitle>
            <HeroHighlights>
              {HERO_HIGHLIGHTS.map((item) => (
                <HeroHighlight key={item}>{item}</HeroHighlight>
              ))}
            </HeroHighlights>
            <HeroActions>
              <PillButton as={Link} to="/main/account-setup">
                {heroCtaLabel}
              </PillButton>
              <DemoButton>Book a Demo</DemoButton>
            </HeroActions>
            {trialEnabled ? (
              <HeroNote>Explore Neat Schedule free for 14 days. No credit card required.</HeroNote>
            ) : (
              <HeroNote>Choose a plan that fits your business and get set up in minutes.</HeroNote>
            )}
          </HeroContent>

          <HeroVisual>
            <HeroGlow aria-hidden />
            <HeroMockupBack>
              <PreviewClickableImage
                src={calendarPreview}
                alt="Neat Schedule calendar and scheduling view"
                ariaLabel="View full-size Neat Schedule calendar preview"
                onClick={() =>
                  openPreview(
                    calendarPreview,
                    'Neat Schedule calendar and scheduling view',
                    'Full-size calendar preview'
                  )
                }
              />
            </HeroMockupBack>
            <BrowserFrame>
              <BrowserBar>
                <BrowserDots aria-hidden>
                  <span />
                  <span />
                  <span />
                </BrowserDots>
                <BrowserUrl>neatschedule.com/app</BrowserUrl>
              </BrowserBar>
              <BrowserScreen>
                <PreviewClickableImage
                  src={overviewPreview}
                  alt="Neat Schedule scheduling dashboard preview"
                  ariaLabel="View full-size Neat Schedule dashboard preview"
                  onClick={() =>
                    openPreview(
                      overviewPreview,
                      'Neat Schedule scheduling dashboard preview',
                      'Full-size dashboard preview'
                    )
                  }
                />
              </BrowserScreen>
            </BrowserFrame>
          </HeroVisual>
        </HeroGrid>
      </HeroContainer>

      <HeroPreviewLightbox
        open={Boolean(lightbox)}
        imageSrc={lightbox?.src}
        imageAlt={lightbox?.alt}
        dialogAriaLabel={lightbox?.dialogAriaLabel}
        onClose={() => setLightbox(null)}
      />
    </Hero>
  );
}
